import Avatar, { AvatarProps } from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FunctionComponent, useEffect, useState } from 'react';
import { Maximize, RotateCw, X, ZoomIn, ZoomOut } from 'lucide';
import Icon from './Icon';
import MuiDialog from './MuiDialog';
import {
  displaySize as computeDisplaySize,
  fitScale,
  MAX_SCALE,
  nextQuarterTurn,
  orientedSize,
  scaleIn,
  scaleOut,
  scalePercent,
  VIEWPORT_RATIO,
  type Size,
} from './imageZoom';

/** Cỡ khung nhìn hiện tại; tách ra để phần render không đụng trực tiếp `window`. */
const currentViewport = (): Size => ({
  width: typeof window === 'undefined' ? 0 : window.innerWidth,
  height: typeof window === 'undefined' ? 0 : window.innerHeight,
});

interface Props extends AvatarProps {
  children?: React.ReactNode;
  isAvatar?: boolean;
  // loadingContent?: React.ReactNode;
}
const ImageDialog: FunctionComponent<Props> = (props: Props) => {
  const {
    children,
    sx,
    src,
    onClick,
    isAvatar,
    // loadingContent,
    alt = 'avatar',
    ...rest
  } = props;
  const [open, setOpen] = useState(false);
  /**
   * Mức phóng so với CỠ GỐC của ảnh (100% = cỡ gốc). `null` = đang theo mức "vừa màn hình", nhờ
   * vậy khi đổi cỡ cửa sổ thì ảnh tự vừa lại thay vì giữ một con số cũ.
   */
  const [scale, setScale] = useState<number | null>(null);
  /** Cỡ gốc của ảnh, lấy khi ảnh tải xong — cần để tính cả mức vừa màn hình lẫn số phần trăm. */
  const [natural, setNatural] = useState<Size | null>(null);
  /** Góc xem: 0, 90, 180, 270. Chỉ xoay lúc xem, không ghi đè file ảnh. */
  const [rotation, setRotation] = useState(0);
  /** Đổi khi cửa sổ đổi cỡ, để tính lại mức vừa màn hình. */
  const [viewport, setViewport] = useState<Size>(() => currentViewport());

  useEffect(() => {
    if (!open) return;
    const onResize = () => setViewport(currentViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  const oriented = natural ? orientedSize(natural, rotation) : null;
  const fit = oriented ? fitScale(oriented, viewport) : 1;
  const effectiveScale = scale ?? fit;
  /** Cỡ thẻ <img> trước khi xoay. */
  const layout = natural ? computeDisplaySize(natural, effectiveScale) : null;
  /** Khung nhìn sau khi xoay — 90°/270° thì rộng/cao đã đổi chỗ. */
  const frame = oriented ? computeDisplaySize(oriented, effectiveScale) : null;

  const openViewer = () => {
    setScale(null); // mỗi lần mở lại bắt đầu ở mức vừa màn hình
    setNatural(null);
    setRotation(0);
    setOpen(true);
  };

  const rotate = () => {
    setRotation((degrees) => nextQuarterTurn(degrees));
    setScale(null);
  };

  return (
    <>
      <Avatar
        variant={isAvatar ? 'circular' : 'square'}
        sx={
          isAvatar
            ? sx
            : {
                height: 'unset',
                width: 'unset',
                cursor: 'pointer',
                ...sx,
              }
        }
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick(e);
          if (src) openViewer();
        }}
        alt={alt}
        src={src}
        {...rest}
      >
        {children}
      </Avatar>
      <MuiDialog
        open={open}
        onClose={setOpen}
        disableCloseBtn
        // maxWidth={false} + m:0: bỏ trần theo breakpoint và lề mặc định của Dialog, để ảnh được
        // phép rộng tới sát khung nhìn thay vì bị bó trong Paper cỡ 'sm'.
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 0,
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
          },
        }}
      >
        <IconButton
          sx={{
            position: 'fixed',
            top: 32,
            right: 32,
            bgcolor: 'white',
            ':hover': {
              bgcolor: 'white',
            },
            color: 'black',
          }}
          onClick={() => setOpen(false)}
          aria-label="Đóng"
        >
          <Icon icon={X} />
        </IconButton>
        {/*
          Dùng <img> trực tiếp, KHÔNG dùng Avatar: Avatar ép ảnh bên trong thành
          `width/height: 100%; object-fit: cover`, nên với height/width 'unset' thì ảnh hiện ở kích
          thước THẬT — ảnh chụp điện thoại vài nghìn pixel tràn ra ngoài khung nhìn, phải cuộn mới
          xem được. Nay đặt cỡ hiển thị = cỡ GỐC × mức phóng (đơn vị px), nên khi phóng thì khung
          dưới đây cuộn được để xem từng phần — thứ mà transform: scale() không làm được vì không
          đổi kích thước layout.
        */}
        <Box
          sx={{
            maxWidth: `${VIEWPORT_RATIO.width * 100}vw`,
            maxHeight: `${VIEWPORT_RATIO.height * 100}vh`,
            overflow: 'auto',
            margin: 'auto',
          }}
        >
          <Box
            sx={{
              width: frame ? `${frame.width}px` : 'auto',
              height: frame ? `${frame.height}px` : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={src}
              alt={alt}
              onLoad={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                setNatural({ width: img.naturalWidth, height: img.naturalHeight });
              }}
              sx={{
                display: 'block',
                flexShrink: 0,
                // Trước khi biết cỡ gốc: bó theo khung nhìn để không lóe ra ảnh cỡ thật.
                width: layout ? `${layout.width}px` : 'auto',
                height: layout ? `${layout.height}px` : 'auto',
                maxWidth: layout ? 'none' : `${VIEWPORT_RATIO.width * 100}vw`,
                maxHeight: layout ? 'none' : `${VIEWPORT_RATIO.height * 100}vh`,
                transform: rotation ? `rotate(${rotation}deg)` : 'none',
              }}
            />
          </Box>
        </Box>

        {/* Nút thu/phóng: mặc định ảnh vừa khung nhìn, ai cần xem chi tiết thì phóng to lên. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'white',
            borderRadius: 999,
            px: 1,
            py: 0.5,
            boxShadow: 3,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setScale(scaleOut(effectiveScale, fit))}
            disabled={effectiveScale <= fit}
            title="Thu nhỏ"
          >
            <Icon icon={ZoomOut} />
          </IconButton>
          {/* Phần trăm so với CỠ GỐC của ảnh: 100% là cỡ gốc, nhỏ hơn cỡ gốc thì dưới 100%. */}
          <Typography variant="body2" sx={{ minWidth: 52, textAlign: 'center', color: 'black' }}>
            {scalePercent(effectiveScale)}%
          </Typography>
          <IconButton
            size="small"
            onClick={() => setScale(scaleIn(effectiveScale, fit))}
            disabled={effectiveScale >= MAX_SCALE}
            title="Phóng to"
          >
            <Icon icon={ZoomIn} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setScale(null)}
            disabled={effectiveScale === fit}
            title="Vừa màn hình"
          >
            <Icon icon={Maximize} />
          </IconButton>
          <IconButton
            size="small"
            onClick={rotate}
            disabled={!natural}
            title="Xoay ảnh"
            aria-label="Xoay ảnh"
          >
            <Icon icon={RotateCw} />
          </IconButton>
        </Stack>
      </MuiDialog>
    </>
  );
};
export default ImageDialog;
